import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ReferenceFilter, ReferenceFilterChips } from '@/components/orders/ReferenceFilter';
import { useReferenceConfig } from '@/hooks/useReferenceConfig';
import { Checkbox } from '@/components/ui/checkbox';
import { slaConfigStageLabel } from '@/lib/slaLabels';
import {
  Timer, AlertTriangle, RefreshCcw, Search, BarChart3, TableIcon,
  Settings2, ExternalLink, ArrowUpRight, Printer, BellOff, Bell, Users, X as XIcon,
  Loader2, Check,
} from 'lucide-react';
import { formatDaysSince } from '@/lib/format';
import { DelayBadge, classifyOrder, DELAY_COLORS, formatHours } from '@/components/production/DelayBadge';
import { GanttRow } from '@/components/production/GanttRow';

// ---------------------------------------------------------------------------
// Wave 3.78 — Production Timeline dashboard page.
// ---------------------------------------------------------------------------
export default function ProductionTimeline() {
  const { user, effectiveCompanyId, isSuperAdmin } = useAuth();
  return (
    <AppShell title="Production Timeline" subtitle="Every active order · every stage · every delay">
      <CompanyGate>
        <TimelineBody companyId={effectiveCompanyId} isSuperAdmin={isSuperAdmin} user={user} />
      </CompanyGate>
    </AppShell>
  );
}

function TimelineBody({ companyId, isSuperAdmin, user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') || 'all';
  const initialSlaOff = searchParams.get('sla_off') === '1';

  const [data, setData] = useState({ counters: { active: 0, on_track: 0, at_risk: 0, delayed: 0, severe: 0, sla_off: 0 }, orders: [], stages: [] });
  const [slaTemplate, setSlaTemplate] = useState({ stages: [] });
  const [goldsmiths, setGoldsmiths] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [slaOff, setSlaOff] = useState(initialSlaOff);
  const [view, setView] = useState('gantt'); // gantt | table
  const [q, setQ] = useState('');
  // Wave 3.99g — Multi-select cascading filters. ``goldsmithIds`` and
  // ``orderIds`` are Sets of ids; empty ⇒ no narrowing on that axis.
  // ``customerId`` stays single-select for now (not part of this ask).
  const [goldsmithIds, setGoldsmithIds] = useState(() => new Set());
  const [orderIds, setOrderIds] = useState(() => new Set());
  const [customerId, setCustomerId] = useState('');
  // Reference-Number system — enabled per company + multi-select filter.
  const { enabled: refEnabled, label: refLabel } = useReferenceConfig(companyId);
  const [referenceNos, setReferenceNos] = useState([]);
  // Wave 3.99g — Legend-driven filter. Nullable string picking exactly
  // ONE of the eight legend keys (see LEGEND_KEYS below). When set,
  // the timeline shows only orders whose classification matches. Kept
  // separate from the delay tab / slaOff toggle so those existing
  // filters can co-exist (a click on the legend syncs both surfaces).
  const [activeLegend, setActiveLegend] = useState(null);
  // Master universe — full unfiltered list used purely for cascade
  // option lists so narrowing one filter never empties its own
  // dropdown.
  const [masterOrders, setMasterOrders] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const canEditTemplate = isSuperAdmin || user?.role === 'company_admin';
  // Sales roles are read-only and blocked from exports server-side — hide the
  // PDF/print affordances so they never hit a 403.
  const isSalesRole = user?.role === 'sales_person' || user?.role === 'sales_manager';

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { company_id: companyId, filter };
      if (q) params.q = q;
      if (goldsmithIds.size > 0) params.goldsmith_ids = Array.from(goldsmithIds).join(',');
      if (customerId) params.customer_id = customerId;
      if (orderIds.size > 0) params.order_ids = Array.from(orderIds).join(',');
      if (slaOff) params.sla_off = 1;
      const { data } = await api.get('/production-timeline/orders', { params });
      setData(data || {});
    } catch {
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  // Wave 3.99g — Fetch the UNFILTERED order universe. Used purely to
  // populate cascade option lists so narrowing one filter never empties
  // its own dropdown. Refreshed only on company change.
  const loadMaster = async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get('/production-timeline/orders', {
        params: { company_id: companyId, filter: 'all' },
      });
      setMasterOrders(data?.orders || []);
    } catch { setMasterOrders([]); }
  };

  const loadTemplate = async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get('/production-timeline/sla-template', { params: { company_id: companyId } });
      setSlaTemplate(data || { stages: [] });
    } catch {
      setSlaTemplate({ stages: [] });
    }
  };

  const loadMasters = async () => {
    if (!companyId) return;
    // Wave 4.10 — Independent fetches (allSettled) so a role lacking access to
    // one master (e.g. Sales roles can't read Goldsmiths) still gets the other.
    const [gs, cs] = await Promise.allSettled([
      api.get('/masters/goldsmiths', { params: { company_id: companyId } }),
      api.get('/masters/customers', { params: { company_id: companyId } }),
    ]);
    setGoldsmiths(gs.status === 'fulfilled' ? (gs.value.data || []) : []);
    setCustomers(cs.status === 'fulfilled' ? (cs.value.data || []) : []);
  };

  useEffect(() => {
    if (!companyId) return;
    load(); loadTemplate(); loadMasters();
  }, [companyId, filter, q, goldsmithIds, customerId, orderIds, slaOff]);
  // Wave 3.99g — Master universe fetched once per company. Kept in a
  // separate effect so filter changes never trigger a re-fetch.
  useEffect(() => {
    if (companyId) loadMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Persist filter in URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (filter && filter !== 'all') params.set('filter', filter); else params.delete('filter');
    if (slaOff) params.set('sla_off', '1'); else params.delete('sla_off');
    setSearchParams(params, { replace: true });
  }, [filter, slaOff]);

  const counters = data.counters || {};
  const orders = data.orders || [];
  const stages = data.stages && data.stages.length ? data.stages : slaTemplate.stages;

  // Wave 3.99g — Legend classification. Returns which colour bucket an
  // order should be counted under. Note an order can qualify for
  // several ("waiting" + "delayed" is common) — we walk in
  // priority order so counters + chip highlights stay intuitive.
  const legendBucketsFor = React.useCallback((o) => {
    const buckets = new Set();
    if (!o) return buckets;
    if ((o.stage || '') === 'Completed') buckets.add('completed');
    // On Track — the timeline is progressing but not late.
    if (!o.is_at_risk && !o.is_delayed && !o.is_severe && (o.stage || '') !== 'Completed') {
      buckets.add('on_track');
    }
    if (o.is_at_risk) buckets.add('at_risk');
    if (o.is_delayed) buckets.add('delayed');
    if (o.is_severe) buckets.add('severe');
    if (Array.isArray(o.blocked_stages) && o.blocked_stages.length > 0) buckets.add('waiting');
    if (!o.current_stage_kind && (o.stage || '') !== 'Completed') buckets.add('not_started');
    const scope = o.sla_scope || {};
    if (scope.timeline === false || scope.procurement === false) buckets.add('sla_off');
    return buckets;
  }, []);

  const orderMatchesLegend = React.useCallback((o, key) => {
    if (!key) return true;
    return legendBucketsFor(o).has(key);
  }, [legendBucketsFor]);

  // Client-side predicate mirroring backend filters + activeLegend.
  // ``exclude`` skips a specific axis so each dropdown / chip can
  // compute its own cascade options.
  const orderMatchesFilters = React.useCallback((o, exclude = {}) => {
    if (!exclude.search && q) {
      const needle = q.trim().toLowerCase();
      const hay = `${o.order_no || ''} ${o.title || ''} ${o.customer_name || ''} ${o.goldsmith_name || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (!exclude.goldsmith && goldsmithIds.size > 0) {
      if (!goldsmithIds.has(o.goldsmith_id)) return false;
    }
    if (!exclude.customer && customerId) {
      if (o.customer_id !== customerId) return false;
    }
    if (!exclude.order && orderIds.size > 0) {
      if (!orderIds.has(o.order_id)) return false;
    }
    if (!exclude.reference && referenceNos.length > 0) {
      if (!referenceNos.includes(o.reference_no)) return false;
    }
    if (!exclude.filter && filter && filter !== 'all') {
      if (filter === 'at_risk' && !o.is_at_risk) return false;
      if (filter === 'delayed' && !o.is_delayed) return false;
      if (filter === 'severe'  && !o.is_severe)  return false;
    }
    if (!exclude.slaOff && slaOff) {
      const scope = o.sla_scope || {};
      if (scope.timeline !== false && scope.procurement !== false) return false;
    }
    if (!exclude.legend && activeLegend) {
      if (!orderMatchesLegend(o, activeLegend)) return false;
    }
    return true;
  }, [q, goldsmithIds, customerId, orderIds, referenceNos, filter, slaOff, activeLegend, orderMatchesLegend]);

  // Cascaded option lists — each excludes its own axis so the
  // narrowing never empties the picker itself. Fall back to full
  // master lists while ``masterOrders`` is still empty.
  const cascadedGoldsmithIds = React.useMemo(() => {
    const set = new Set();
    for (const o of masterOrders) {
      if (orderMatchesFilters(o, { goldsmith: true }) && o.goldsmith_id) set.add(o.goldsmith_id);
    }
    return set;
  }, [masterOrders, orderMatchesFilters]);
  const cascadedCustomerIds = React.useMemo(() => {
    const set = new Set();
    for (const o of masterOrders) {
      if (orderMatchesFilters(o, { customer: true }) && o.customer_id) set.add(o.customer_id);
    }
    return set;
  }, [masterOrders, orderMatchesFilters]);
  const cascadedGoldsmiths = React.useMemo(() => {
    if (!masterOrders.length) return goldsmiths;
    return (goldsmiths || [])
      .filter((g) => cascadedGoldsmithIds.has(g.id) || goldsmithIds.has(g.id));
  }, [goldsmiths, cascadedGoldsmithIds, goldsmithIds, masterOrders.length]);
  const cascadedCustomers = React.useMemo(() => {
    if (!masterOrders.length) return customers;
    return (customers || [])
      .filter((c) => cascadedCustomerIds.has(c.id) || c.id === customerId);
  }, [customers, cascadedCustomerIds, customerId, masterOrders.length]);
  // Cascaded orders (for the Order-No multi-select). Uses the master
  // universe filtered by all OTHER active filters, so narrowing
  // Goldsmith immediately shrinks the Order dropdown to that
  // goldsmith's orders only.
  const cascadedOrderOptions = React.useMemo(() => {
    if (!masterOrders.length) return [];
    return masterOrders
      .filter((o) => orderMatchesFilters(o, { order: true }))
      .map((o) => ({
        id: o.order_id,
        label: `${o.order_no}${o.title ? ` — ${o.title}` : ''}`,
        sub: `${o.customer_name || '—'} · ${o.goldsmith_name || '—'}`,
      }));
  }, [masterOrders, orderMatchesFilters]);
  // Per-legend-key match counts (respect other filters — cascade the
  // legend). A count of 0 disables the chip.
  const legendCounts = React.useMemo(() => {
    const counts = { completed: 0, on_track: 0, at_risk: 0, delayed: 0, severe: 0, waiting: 0, not_started: 0, sla_off: 0 };
    if (!masterOrders.length) return counts;
    for (const o of masterOrders) {
      if (!orderMatchesFilters(o, { legend: true, filter: true, slaOff: true })) continue;
      const b = legendBucketsFor(o);
      for (const k of Object.keys(counts)) if (b.has(k)) counts[k] += 1;
    }
    return counts;
  }, [masterOrders, orderMatchesFilters, legendBucketsFor]);

  // Chip click handler — syncs the shared filter / slaOff / activeLegend
  // state so a single click behaves the same whether the operator
  // reaches for the legend, the delay tabs, or the SLA-off toggle.
  // A second click on the same key clears the filter.
  const handleLegendClick = (key) => {
    const toggling = (curr, val) => (curr === val ? null : val);
    if (key === 'at_risk' || key === 'delayed' || key === 'severe') {
      setFilter((prev) => (prev === key ? 'all' : key));
      setSlaOff(false);
      setActiveLegend(null);
      return;
    }
    if (key === 'sla_off') {
      setSlaOff((v) => !v);
      setFilter('all');
      setActiveLegend(null);
      return;
    }
    // completed | on_track | waiting | not_started — client-only
    setActiveLegend((prev) => toggling(prev, key));
    setFilter('all');
    setSlaOff(false);
  };

  // Which legend key is currently "active" for the highlight glow.
  const activeLegendKey = (() => {
    if (activeLegend) return activeLegend;
    if (slaOff) return 'sla_off';
    if (filter === 'at_risk') return 'at_risk';
    if (filter === 'delayed') return 'delayed';
    if (filter === 'severe')  return 'severe';
    return null;
  })();

  // Client-side legend narrowing on top of the server list.
  const visibleOrders = React.useMemo(() => {
    const refSet = new Set(referenceNos || []);
    return (orders || []).filter((o) => {
      if (activeLegend && !orderMatchesLegend(o, activeLegend)) return false;
      if (refSet.size > 0 && !refSet.has(o.reference_no)) return false;
      return true;
    });
  }, [orders, activeLegend, orderMatchesLegend, referenceNos]);

  // Reference-Number system — distinct reference numbers present in the
  // company's timeline universe (for the reference filter options).
  const referenceOptions = React.useMemo(() => {
    if (!refEnabled) return [];
    const set = new Set();
    for (const o of masterOrders) {
      if (o.reference_no != null) set.add(o.reference_no);
    }
    return Array.from(set);
  }, [refEnabled, masterOrders]);

  return (
    <div className="space-y-5">
      {/* Header + Manage Template CTA */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="text-[15px] font-medium text-[var(--ink-navy)]">Production Floor Overview</div>
          <div className="text-xs text-slate-500">Delayed items are surfaced first; use filters to drill into a stage or goldsmith.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { load(); loadTemplate(); }} data-testid="prod-timeline-refresh-button">
            <RefreshCcw size={14} className="mr-1.5" /> Refresh
          </Button>
          {canEditTemplate && (
            <Button size="sm" onClick={() => setTemplateDialogOpen(true)} data-testid="prod-timeline-manage-template-button">
              <Settings2 size={14} className="mr-1.5" /> Manage SLA Template
            </Button>
          )}
        </div>
      </div>

      {/* Counters row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <CounterCard label="Active" value={counters.active || 0} icon={<Timer size={14} />} testId="counter-active" />
        <CounterCard label="On Track" value={counters.on_track || 0} tone="on_track" testId="counter-on-track" />
        <CounterCard label="At Risk" value={counters.at_risk || 0} tone="at_risk" testId="counter-at-risk" />
        <CounterCard label="Delayed" value={counters.delayed || 0} tone="delayed" testId="counter-delayed" />
        <CounterCard label="Critical (2× SLA)" value={counters.severe || 0} tone="severe" icon={<AlertTriangle size={14} />} testId="counter-severe" />
      </div>

      {/* Filter bar */}
      <Card className="p-3 sticky-filter-bar" data-testid="prod-timeline-filter-bar">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1 grid gap-3 md:grid-cols-4">
            <div>
              <Label className="text-[11px] text-slate-500">Search</Label>
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Order #, title, customer, goldsmith…"
                  className="pl-7 h-9"
                  data-testid="prod-timeline-search-input"
                />
              </div>
            </div>
            {/* Wave 3.99g — Goldsmith multi-select with cascaded options. */}
            <div>
              <Label className="text-[11px] text-slate-500">Goldsmith</Label>
              <PTMultiSelect
                label="Goldsmith"
                testId="prod-timeline-goldsmith-multi"
                options={cascadedGoldsmiths.map((g) => ({ id: g.id, label: g.name }))}
                selectedIds={goldsmithIds}
                onChange={setGoldsmithIds}
                placeholder="Search goldsmith…"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Customer</Label>
              <SearchableSelect
                items={[{ id: 'all', name: 'All customers' }, ...cascadedCustomers]}
                value={customerId || 'all'}
                onValueChange={(v) => setCustomerId(!v || v === 'all' ? '' : v)}
                itemToLabel={(c) => c.name}
                searchKeys={['name']}
                placeholder="All customers"
                searchPlaceholder="Search customer…"
                testId="prod-timeline-customer-select"
              />
            </div>
            {/* Wave 3.99g — Order-No multi-select with cascaded options. */}
            <div>
              <Label className="text-[11px] text-slate-500">Order No</Label>
              <PTMultiSelect
                label="Orders"
                testId="prod-timeline-orders-multi"
                options={cascadedOrderOptions}
                selectedIds={orderIds}
                onChange={setOrderIds}
                placeholder="Search order no or title…"
              />
            </div>
            {/* Reference-Number system — multi-select reference filter. */}
            {refEnabled && (
              <div>
                <Label className="text-[11px] text-slate-500">{refLabel}</Label>
                <ReferenceFilter
                  options={referenceOptions}
                  value={referenceNos}
                  onChange={setReferenceNos}
                  label={refLabel}
                  testIdPrefix="prod-timeline-reference-filter"
                />
              </div>
            )}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[11px] text-slate-500 block mb-1">Filter</Label>
              <Tabs value={filter} onValueChange={(v) => { setFilter(v); setActiveLegend(null); }}>
                <TabsList>
                  <TabsTrigger value="all" data-testid="filter-tab-all">All</TabsTrigger>
                  <TabsTrigger value="at_risk" data-testid="filter-tab-at-risk">At Risk</TabsTrigger>
                  <TabsTrigger value="delayed" data-testid="filter-tab-delayed">Delayed</TabsTrigger>
                  <TabsTrigger value="severe" data-testid="filter-tab-severe">Critical</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div>
              <Label className="text-[11px] text-slate-500 block mb-1">View</Label>
              <Tabs value={view} onValueChange={setView}>
                <TabsList>
                  <TabsTrigger value="gantt" data-testid="view-tab-gantt"><BarChart3 size={13} className="mr-1" />Gantt</TabsTrigger>
                  <TabsTrigger value="table" data-testid="view-tab-table"><TableIcon size={13} className="mr-1" />Table</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>
        {/* Wave 3.83 — SLA Off filter chip */}
        {/* Wave 3.99g — Active filter chip row. Renders every active
            narrowing (SLA Off, Goldsmiths, Orders, Legend) as a
            removable chip so the operator can peel filters off one by
            one. */}
        {(slaOff || goldsmithIds.size > 0 || orderIds.size > 0 || (referenceNos || []).length > 0 || activeLegend) && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="prod-timeline-filter-chips">
            {slaOff && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border bg-stone-100 text-stone-700 border-stone-300"
                data-testid="sla-off-filter-chip"
              >
                <BellOff size={11} />
                Showing SLA-off orders ({counters.sla_off || 0})
                <button
                  type="button"
                  onClick={() => setSlaOff(false)}
                  className="ml-1 hover:text-[var(--ink-navy)]"
                  aria-label="Clear SLA-off filter"
                  data-testid="sla-off-filter-clear"
                >
                  <XIcon size={11} />
                </button>
              </span>
            )}
            {goldsmithIds.size > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border bg-white text-slate-700 border-[var(--divider)]">
                <Users size={11} />
                {goldsmithIds.size === 1
                  ? `Goldsmith: ${(goldsmiths.find((g) => goldsmithIds.has(g.id)) || {}).name || '—'}`
                  : `${goldsmithIds.size} goldsmiths`}
                <button
                  type="button"
                  onClick={() => setGoldsmithIds(new Set())}
                  className="ml-1 hover:text-[var(--ink-navy)]"
                  aria-label="Clear goldsmith filter"
                  data-testid="goldsmith-filter-clear"
                >
                  <XIcon size={11} />
                </button>
              </span>
            )}
            {orderIds.size > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border bg-white text-slate-700 border-[var(--divider)]">
                {orderIds.size === 1 ? '1 order' : `${orderIds.size} orders`}
                <button
                  type="button"
                  onClick={() => setOrderIds(new Set())}
                  className="ml-1 hover:text-[var(--ink-navy)]"
                  aria-label="Clear order filter"
                  data-testid="order-filter-clear"
                >
                  <XIcon size={11} />
                </button>
              </span>
            )}
            <ReferenceFilterChips
              value={referenceNos}
              onChange={setReferenceNos}
              label={refLabel}
              testIdPrefix="prod-timeline-reference-chip"
            />
            {activeLegend && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border bg-[var(--ink-navy)]/5 text-[var(--ink-navy)] border-[var(--ink-navy)]/20">
                Legend: {(() => {
                  const map = { completed: 'Completed', on_track: 'On Track', waiting: 'Waiting', not_started: 'Not Started' };
                  return map[activeLegend] || activeLegend;
                })()}
                <button
                  type="button"
                  onClick={() => setActiveLegend(null)}
                  className="ml-1 hover:text-[var(--gold-accent)]"
                  aria-label="Clear legend filter"
                  data-testid="legend-filter-clear"
                >
                  <XIcon size={11} />
                </button>
              </span>
            )}
          </div>
        )}
      </Card>

      {/* Body */}
      {loading ? (
        <Card className="p-6 text-center text-sm text-slate-500" data-testid="prod-timeline-loading">Loading production floor…</Card>
      ) : visibleOrders.length === 0 ? (
        <Card className="p-8 text-center" data-testid="prod-timeline-empty">
          <div className="text-sm text-slate-600">No matching orders.</div>
          <div className="text-xs text-slate-400 mt-1">Try clearing the filter or search.</div>
        </Card>
      ) : view === 'gantt' ? (
        <Card className="p-0 overflow-hidden">
          {/* Wave 3.94 — Colour legend row */}
          <ColorLegend
            activeKey={activeLegendKey}
            counts={legendCounts}
            onToggle={handleLegendClick}
          />
          {/* Stage legend row */}
          <StageLegend stages={stages} />
          <div className="divide-y divide-[var(--divider)]">
            {visibleOrders.map((o) => (
              <GanttRow
                key={o.order_id}
                order={o}
                stages={stages}
                sla_template={slaTemplate.stages}
                onClick={() => navigate(`/orders/${o.order_id}`)}
              />
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[130px]">Order #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Goldsmith</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="w-[80px]">Priority</TableHead>
                  <TableHead>Current Stage</TableHead>
                  <TableHead className="text-right">Time in Stage</TableHead>
                  <TableHead className="text-right">Total Age</TableHead>
                  <TableHead className="text-right">Delayed by</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  {!isSalesRole && <TableHead className="w-[60px] text-right">PDF</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleOrders.map((o) => (
                  <TableRow
                    key={o.order_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/orders/${o.order_id}`)}
                    data-testid={`prod-timeline-table-row-${o.order_no}`}
                  >
                    <TableCell className="font-mono tabular-nums text-[12px]">
                      {/* Wave 3.99g — Table View parity with Gantt: stack
                          Order No + Title so both views read alike. */}
                      <div className="text-[12px] text-[var(--ink-navy)]">{o.order_no}</div>
                      {o.title && (
                        <div className="text-[11px] font-sans font-normal text-slate-600 truncate max-w-[220px]" title={o.title}>
                          {o.title}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-[13px] text-slate-700 truncate">{o.customer_name || '—'}</TableCell>
                    <TableCell className="text-[13px] text-slate-700 truncate">{o.goldsmith_name || '—'}</TableCell>
                    <TableCell className="text-[12px] text-slate-500 truncate">{o.jewellery_type || '—'}</TableCell>
                    <TableCell className="text-[12px] capitalize text-slate-600">{o.priority || 'normal'}</TableCell>
                    <TableCell className="text-[12px] text-[var(--ink-navy)]">
                      {o.current_stage_label || '—'}
                      {(o.blocked_stages || []).length > 0 && (
                        <div className="text-[10px] text-stone-500 mt-0.5">
                          Waiting: {o.blocked_stages.map((b) => b.label).join(', ')}
                        </div>
                      )}
                      {o.requirements_progress && o.requirements_progress.total > 0
                        && (o.current_stage_kind === 'fulfilling_requirements'
                          || (o.blocked_stages || []).some((b) => b.blocked_by === 'fulfilling_requirements')) && (
                        <div className="text-[10px] mt-0.5">
                          <span
                            className="inline-flex items-center gap-1 font-mono tabular-nums px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800"
                            title={`${o.requirements_progress.completed} of ${o.requirements_progress.total} requirements fulfilled`}
                            data-testid={`prod-timeline-progress-chip-${o.order_no}`}
                          >
                            {o.requirements_progress.completed}/{o.requirements_progress.total} reqs
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-[12px]">{formatHours(o.current_stage_hours)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-[12px] text-slate-500">{formatHours(o.total_hours)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-[12px]">
                      <span style={{ color: (o.is_delayed ? DELAY_COLORS.delayed.text : 'inherit') }}>
                        {o.delayed_by_hours > 0 ? formatHours(o.delayed_by_hours) : '—'}
                      </span>
                    </TableCell>
                    <TableCell><DelayBadge order={o} /></TableCell>
                    {!isSalesRole && (
                    <TableCell className="text-right">
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await api.get(
                              `/production-timeline/orders/${o.order_id}/pdf`,
                              { responseType: 'blob' },
                            );
                            const url = URL.createObjectURL(res.data);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `timeline-${o.order_no}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } catch {
                            toast.error('Failed to download PDF');
                          }
                        }}
                        className="p-1.5 rounded-md text-slate-500 hover:text-[var(--ink-navy)] hover:bg-slate-100"
                        title="Print timeline PDF"
                        data-testid={`prod-timeline-print-${o.order_no}`}
                      >
                        <Printer size={14} />
                      </button>
                    </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {templateDialogOpen && (
        <SlaTemplateDialog
          open={templateDialogOpen}
          onOpenChange={setTemplateDialogOpen}
          companyId={companyId}
          onSaved={loadTemplate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wave 3.99g — Local multi-select popover used by the Production
// Timeline filter bar. Mirrors the one in GoldsmithDetail so both
// pages read/behave identically. Options are cascaded upstream — this
// component only renders. Selection is a Set, ``onChange`` receives a
// new Set every mutation.
// ---------------------------------------------------------------------------
function PTMultiSelect({ label, testId, options, selectedIds, onChange, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      (o.label || '').toLowerCase().includes(q) || (o.sub || '').toLowerCase().includes(q),
    );
  }, [options, query]);
  const visibleSelectedCount = useMemo(
    () => options.reduce((n, o) => n + (selectedIds.has(o.id) ? 1 : 0), 0),
    [options, selectedIds],
  );
  const allSelected = options.length > 0 && visibleSelectedCount === options.length;
  const toggleAll = () => {
    const s = new Set(selectedIds);
    if (allSelected) {
      for (const o of options) s.delete(o.id);
    } else {
      for (const o of options) s.add(o.id);
    }
    onChange(s);
  };
  const toggle = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    onChange(s);
  };
  const triggerText = selectedIds.size === 0
    ? `${label}: All`
    : allSelected
      ? `${label}: All (${options.length})`
      : `${label}: ${visibleSelectedCount} of ${options.length}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-9 justify-between w-full"
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate text-left text-[13px]">{triggerText}</span>
          <ArrowUpRight size={12} className="ml-2 opacity-60 shrink-0 rotate-90" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid={`${testId}-content`}>
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder || 'Search…'}
              className="h-8 pl-8 text-[12px]"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px]">
            <button
              type="button"
              onClick={toggleAll}
              className="text-[var(--ink-navy)] hover:underline"
              data-testid={`${testId}-toggle-all`}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
            <span className="text-slate-400">{visibleSelectedCount} of {options.length}</span>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <div className="text-[12px] text-slate-500 px-2 py-3">No matches.</div>
          )}
          {filtered.map((opt) => {
            const active = selectedIds.has(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(opt.id)}
                className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-50"
                data-testid={`${testId}-option-${opt.id}`}
              >
                <Checkbox checked={active} onCheckedChange={() => toggle(opt.id)} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-medium text-slate-800 truncate">{opt.label}</div>
                  {opt.sub && (
                    <div className="text-[11px] text-slate-500 truncate">{opt.sub}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Counter Card
// ---------------------------------------------------------------------------
function CounterCard({ label, value, tone, icon, testId }) {
  const tokens = tone && DELAY_COLORS[tone] ? DELAY_COLORS[tone] : null;
  return (
    <Card
      className="p-3 flex items-center gap-3"
      style={tokens ? { borderLeft: `4px solid ${tokens.text}` } : undefined}
      data-testid={testId}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
        <div
          className="text-2xl font-medium font-mono tabular-nums leading-none mt-1"
          style={tokens ? { color: tokens.text } : { color: 'var(--ink-navy)' }}
        >
          {value}
        </div>
      </div>
      {icon && (
        <div
          className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
          style={tokens ? { background: tokens.bg, color: tokens.text } : { background: 'var(--paper-warm)', color: 'var(--ink-navy)' }}
        >
          {icon}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Stage legend (top row of Gantt view)
// ---------------------------------------------------------------------------
function StageLegend({ stages }) {
  return (
    <div className="grid grid-cols-[260px_1fr_140px] gap-3 items-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-[var(--divider)]">
      <div>Order</div>
      <div className="flex items-center gap-1">
        {(stages || []).map((s, i) => (
          <React.Fragment key={s.kind}>
            <span className="whitespace-nowrap">{(s.label || '').split(' ')[0]}</span>
            {i < (stages.length - 1) && <span className="text-slate-300">·</span>}
          </React.Fragment>
        ))}
      </div>
      <div className="text-right">Current Stage</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wave 3.94 — Colour legend for the Gantt view.
// ---------------------------------------------------------------------------
// A single compact strip that explains every colour / pattern used on a
// Gantt bar. Placed between the filter row and the stage legend so
// operators (especially new users) can decode the timeline at a glance
// without hunting through docs. Uses ``DELAY_COLORS`` as the single
// source of truth so the legend always stays in sync with the actual
// rendered segments.
// Wave 3.99g — Chips are now clickable. Clicking toggles that
// stage-colour bucket as the timeline's filter (a second click clears
// it). Each chip also shows a live count that cascades with the
// other active filters.
// ---------------------------------------------------------------------------
function ColorLegendChip({ chipKey, swatchStyle, label, description, active, disabled, count, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle && onToggle(chipKey)}
      disabled={disabled || !onToggle}
      title={description}
      aria-pressed={!!active}
      className={[
        'inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[13px] whitespace-nowrap transition-colors duration-150',
        active
          ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white shadow-sm'
          : 'border-[var(--divider)] bg-white text-slate-700 hover:bg-slate-50',
        (disabled && !active) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
      data-testid={`gantt-legend-chip-${chipKey}`}
      data-active={active ? '1' : '0'}
    >
      <span
        className="inline-block w-5 h-5 rounded border shrink-0"
        style={swatchStyle}
        aria-hidden="true"
      />
      <span className="font-medium">{label}</span>
      {typeof count === 'number' && (
        <span
          className={[
            'ml-0.5 inline-flex items-center justify-center text-[11px] font-mono tabular-nums rounded-full min-w-[18px] h-[18px] px-1',
            active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600',
          ].join(' ')}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ColorLegend({ activeKey, counts, onToggle }) {
  const dc = DELAY_COLORS;
  const items = [
    {
      chipKey: 'completed',
      label: 'Completed',
      swatchStyle: { background: dc.on_track.bg, borderColor: dc.on_track.border },
      description: 'Stage finished on time.',
    },
    {
      chipKey: 'on_track',
      label: 'On Track',
      swatchStyle: { background: dc.active.bg, borderColor: dc.active.border },
      description: 'Stage currently active and inside its SLA window.',
    },
    {
      chipKey: 'at_risk',
      label: 'At Risk',
      swatchStyle: { background: dc.at_risk.bg, borderColor: dc.at_risk.border },
      description: 'Order is nearing its SLA threshold — needs attention soon.',
    },
    {
      chipKey: 'delayed',
      label: 'Delayed',
      swatchStyle: { background: dc.delayed.bg, borderColor: dc.delayed.border },
      description: 'Order has crossed its SLA target.',
    },
    {
      chipKey: 'severe',
      label: 'Critical (2× SLA)',
      swatchStyle: { background: dc.severe.bg, borderColor: dc.severe.border },
      description: 'Order has crossed twice the SLA window — escalate immediately.',
    },
    {
      chipKey: 'waiting',
      label: 'Waiting',
      swatchStyle: {
        background: `repeating-linear-gradient(-45deg, ${dc.blocked.bg}, ${dc.blocked.bg} 3px, ${dc.blocked.border} 3px, ${dc.blocked.border} 4px)`,
        borderColor: dc.blocked.border,
      },
      description: 'Stage is blocked — a prerequisite step needs to be completed first.',
    },
    {
      chipKey: 'not_started',
      label: 'Not Started',
      swatchStyle: { background: dc.not_started.bg, borderColor: dc.not_started.border },
      description: 'Stage has not been reached yet.',
    },
    {
      chipKey: 'sla_off',
      label: 'SLA Off',
      swatchStyle: { background: dc.sla_off.bg, borderColor: dc.sla_off.border },
      description: 'SLA tracking is intentionally paused for this order.',
    },
  ];
  return (
    <div
      className="flex items-center flex-wrap gap-x-3 gap-y-2 px-4 py-3 border-b border-[var(--divider)] bg-slate-50/60"
      data-testid="gantt-color-legend"
    >
      <span className="text-[12px] font-semibold uppercase tracking-wider text-slate-600 mr-1">
        Legend
      </span>
      {items.map((it) => {
        const c = counts ? counts[it.chipKey] : undefined;
        return (
          <ColorLegendChip
            key={it.chipKey}
            {...it}
            active={activeKey === it.chipKey}
            disabled={typeof c === 'number' && c === 0 && activeKey !== it.chipKey}
            count={c}
            onToggle={onToggle}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manage SLA Template Dialog (Wave 3.78 + Wave 3.79 per-jewellery-type + Wave 3.85 procurement rows + Wave 3.93 notify defaults)
// ---------------------------------------------------------------------------
// Shallow list equality — used everywhere in the dialog to decide
// whether a per-jewellery-type row genuinely overrides the base.
function arraysEqual(a, b) {
  const aa = a || [];
  const bb = b || [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i += 1) {
    if (aa[i] !== bb[i]) return false;
  }
  return true;
}

// Compact avatar chip for a user. Used inside the NotifyPicker trigger
// so operators can see WHO gets pinged without opening the popover.
function initials(nameOrEmail) {
  const s = (nameOrEmail || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  const email = s.split('@')[0];
  return email.slice(0, 2).toUpperCase();
}

function NotifyPicker({ users, value, onChange, hasOverride, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedIds = value || [];
  const selectedUsers = (users || []).filter((u) => selectedIds.includes(u.id));
  const filtered = (users || []).filter((u) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const toggle = (uid) => {
    const cur = new Set(selectedIds);
    if (cur.has(uid)) cur.delete(uid);
    else cur.add(uid);
    onChange(Array.from(cur));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`h-8 inline-flex items-center gap-1.5 px-2 rounded-md border text-[11px] whitespace-nowrap transition-colors ${
            hasOverride
              ? 'border-[var(--gold-accent)] bg-[var(--paper-warm)] text-[var(--ink-navy)]'
              : selectedUsers.length > 0
              ? 'border-[var(--divider)] bg-white text-[var(--ink-navy)] hover:border-slate-400'
              : 'border-dashed border-slate-300 bg-white text-slate-500 hover:text-[var(--ink-navy)] hover:border-slate-400'
          }`}
          data-testid={testId}
          title={selectedUsers.length > 0
            ? `Notifies: ${selectedUsers.map((u) => u.name || u.email).join(', ')}`
            : 'No default recipients — click to configure'}
        >
          <Bell size={12} className={selectedUsers.length > 0 ? 'text-[var(--gold-accent)]' : 'text-slate-400'} />
          {selectedUsers.length === 0 ? (
            <span>Notify</span>
          ) : (
            <>
              {selectedUsers.slice(0, 2).map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--ink-navy)] text-white text-[8px] font-semibold"
                >
                  {initials(u.name || u.email)}
                </span>
              ))}
              {selectedUsers.length > 2 && (
                <span className="text-[10px] text-slate-500 font-mono tabular-nums">+{selectedUsers.length - 2}</span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid={`${testId}-popover`}>
        <div className="p-2 border-b border-[var(--divider)]">
          <div className="text-[11px] font-medium text-[var(--ink-navy)] flex items-center gap-1.5">
            <Users size={12} /> Notify users
            {selectedUsers.length > 0 && (
              <span className="ml-auto text-[10px] text-slate-500 font-mono tabular-nums">
                {selectedUsers.length} selected
              </span>
            )}
          </div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or email…"
            className="h-7 mt-2 text-[11px]"
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-[11px] text-slate-500">
              {(users || []).length === 0 ? 'No users found for this company.' : 'No match.'}
            </div>
          ) : (
            filtered.map((u) => {
              const checked = selectedIds.includes(u.id);
              return (
                <button
                  type="button"
                  key={u.id}
                  onClick={() => toggle(u.id)}
                  className={`flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-slate-50 ${
                    checked ? 'bg-slate-50' : ''
                  }`}
                  data-testid={`${testId}-user-${u.id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(u.id)}
                    className="h-3.5 w-3.5 pointer-events-none"
                    tabIndex={-1}
                  />
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--ink-navy)] text-white text-[9px] font-semibold shrink-0">
                    {initials(u.name || u.email)}
                  </span>
                  <span className="flex-1 min-w-0">
                    <div className="text-[12px] text-[var(--ink-navy)] truncate">{u.name || u.email}</div>
                    <div className="text-[10px] text-slate-500 truncate">{u.email}{u.role ? ` · ${u.role}` : ''}</div>
                  </span>
                </button>
              );
            })
          )}
        </div>
        {selectedUsers.length > 0 && (
          <div className="p-2 border-t border-[var(--divider)] flex items-center justify-between">
            <div className="flex flex-wrap gap-1 flex-1 min-w-0">
              {selectedUsers.slice(0, 3).map((u) => (
                <Badge key={u.id} variant="secondary" className="text-[9px] px-1.5 py-0.5 gap-1">
                  {u.name || u.email}
                  <button
                    type="button"
                    onClick={() => toggle(u.id)}
                    className="hover:text-red-600"
                    aria-label={`Remove ${u.name || u.email}`}
                  >
                    <XIcon size={9} />
                  </button>
                </Badge>
              ))}
              {selectedUsers.length > 3 && (
                <span className="text-[10px] text-slate-500 font-mono tabular-nums">+{selectedUsers.length - 3}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[10px] text-slate-500 hover:text-red-600 ml-2 whitespace-nowrap"
              data-testid={`${testId}-clear`}
            >
              Clear all
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function SlaTemplateDialog({ open, onOpenChange, companyId, onSaved }) {
  const [tpl, setTpl] = useState({
    stages: [],
    by_jewellery_type: {},
    procurement_defaults: [],
    procurement_by_jewellery_type: {},
    order_raised_notify_user_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [jewelleryTypes, setJewelleryTypes] = useState([]);
  const [activeType, setActiveType] = useState('__base__'); // '__base__' = default template
  // Wave 3.93 — Company users available as notify recipients.
  const [users, setUsers] = useState([]);
  // Wave 4.x — When checked at save, replace notify recipients on all active
  // orders so existing jobs stay in lock-step with the template.
  const [backfill, setBackfill] = useState(false);
  // Wave 4.x — Blast-radius preview: {total_active, will_change} | null.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;
    setBackfill(false);
    (async () => {
      try {
        const [tplRes, typesRes, usersRes] = await Promise.all([
          api.get('/production-timeline/sla-template', { params: { company_id: companyId } }),
          api.get('/jewellery-types', { params: { company_id: companyId } }),
          api.get('/production-timeline/users', { params: { company_id: companyId } }),
        ]);
        setTpl(tplRes.data || {
          stages: [], by_jewellery_type: {},
          procurement_defaults: [], procurement_by_jewellery_type: {},
        });
        setJewelleryTypes(typesRes.data || []);
        setUsers(usersRes.data || []);
      } catch {
        setTpl({
          stages: [], by_jewellery_type: {},
          procurement_defaults: [], procurement_by_jewellery_type: {},
        });
      }
    })();
  }, [open, companyId]);

  const updateBaseDays = (kind, val) => {
    setTpl((prev) => ({
      ...prev,
      stages: (prev.stages || []).map((s) => s.kind === kind ? { ...s, sla_days: Number(val) || 0 } : s),
    }));
  };

  // Wave 3.93 — Base notify list updater. Storing an empty array is
  // fine — the backend treats it as "no default recipients".
  const updateBaseNotify = (kind, uids) => {
    setTpl((prev) => ({
      ...prev,
      stages: (prev.stages || []).map((s) =>
        s.kind === kind ? { ...s, notify_user_ids: [...(uids || [])] } : s,
      ),
    }));
  };

  // Wave 3.99ah — Global "Order Raised" notify list (not per-type, not per-stage).
  const updateOrderRaisedNotify = (uids) => {
    setTpl((prev) => ({ ...prev, order_raised_notify_user_ids: [...(uids || [])] }));
  };

  const updateTypeOverride = (typeName, kind, val) => {
    setTpl((prev) => {
      const byType = { ...(prev.by_jewellery_type || {}) };
      const cur = [...(byType[typeName] || [])];
      const parsed = Number(val);
      const idx = cur.findIndex((s) => s.kind === kind);
      const baseRow = (prev.stages || []).find((s) => s.kind === kind);
      const baseDays = Number(baseRow?.sla_days || 0);
      const baseNotify = baseRow?.notify_user_ids || [];
      if (val === '' || val === null || val === undefined || Number.isNaN(parsed)) {
        // Empty -> drop the days override. If the row still has a
        // notify override we keep it (with SLA back at base value);
        // otherwise remove the row completely.
        if (idx >= 0) {
          const cur_notify = cur[idx].notify_user_ids || [];
          if (arraysEqual(cur_notify, baseNotify)) cur.splice(idx, 1);
          else cur[idx] = { ...cur[idx], sla_days: baseDays };
        }
      } else {
        const label = baseRow?.label || kind;
        if (idx >= 0) cur[idx] = { ...cur[idx], sla_days: parsed };
        else cur.push({ kind, label, sla_days: parsed, notify_user_ids: baseNotify });
      }
      if (cur.length === 0) delete byType[typeName];
      else byType[typeName] = cur;
      return { ...prev, by_jewellery_type: byType };
    });
  };

  // Wave 3.93 — Set per-type override for notify recipients. If the
  // resulting list matches the base AND the row has no SLA-days override,
  // we drop the row so the type genuinely inherits.
  const updateTypeOverrideNotify = (typeName, kind, uids) => {
    setTpl((prev) => {
      const byType = { ...(prev.by_jewellery_type || {}) };
      const cur = [...(byType[typeName] || [])];
      const idx = cur.findIndex((s) => s.kind === kind);
      const baseRow = (prev.stages || []).find((s) => s.kind === kind);
      const baseDays = Number(baseRow?.sla_days || 0);
      const baseNotify = baseRow?.notify_user_ids || [];
      const label = baseRow?.label || kind;
      const cleaned = [...(uids || [])];
      if (idx >= 0) {
        // Update in place.
        cur[idx] = { ...cur[idx], notify_user_ids: cleaned };
        const stillOverridesDays = Number(cur[idx].sla_days || 0) !== baseDays;
        const stillOverridesNotify = !arraysEqual(cleaned, baseNotify);
        if (!stillOverridesDays && !stillOverridesNotify) {
          cur.splice(idx, 1);
        }
      } else if (!arraysEqual(cleaned, baseNotify)) {
        cur.push({ kind, label, sla_days: baseDays, notify_user_ids: cleaned });
      }
      if (cur.length === 0) delete byType[typeName];
      else byType[typeName] = cur;
      return { ...prev, by_jewellery_type: byType };
    });
  };

  // Wave 3.85 — Procurement row editors (parallel to stage editors above
  // but writing to the new ``procurement_defaults`` / ``procurement_by_jewellery_type``
  // fields on the template).
  const updateProcurementBase = (kind, val) => {
    setTpl((prev) => ({
      ...prev,
      procurement_defaults: (prev.procurement_defaults || []).map((s) =>
        s.kind === kind ? { ...s, sla_days: Number(val) || 0 } : s,
      ),
    }));
  };

  const updateProcurementBaseNotify = (kind, uids) => {
    setTpl((prev) => ({
      ...prev,
      procurement_defaults: (prev.procurement_defaults || []).map((s) =>
        s.kind === kind ? { ...s, notify_user_ids: [...(uids || [])] } : s,
      ),
    }));
  };

  const updateProcurementTypeOverride = (typeName, kind, val) => {
    setTpl((prev) => {
      const byType = { ...(prev.procurement_by_jewellery_type || {}) };
      const cur = [...(byType[typeName] || [])];
      const parsed = Number(val);
      const idx = cur.findIndex((s) => s.kind === kind);
      const baseRow = (prev.procurement_defaults || []).find((s) => s.kind === kind);
      const baseDays = Number(baseRow?.sla_days || 0);
      const baseNotify = baseRow?.notify_user_ids || [];
      if (val === '' || val === null || val === undefined || Number.isNaN(parsed)) {
        if (idx >= 0) {
          const cur_notify = cur[idx].notify_user_ids || [];
          if (arraysEqual(cur_notify, baseNotify)) cur.splice(idx, 1);
          else cur[idx] = { ...cur[idx], sla_days: baseDays };
        }
      } else {
        const label = baseRow?.label || kind;
        if (idx >= 0) cur[idx] = { ...cur[idx], sla_days: parsed };
        else cur.push({ kind, label, sla_days: parsed, notify_user_ids: baseNotify });
      }
      if (cur.length === 0) delete byType[typeName];
      else byType[typeName] = cur;
      return { ...prev, procurement_by_jewellery_type: byType };
    });
  };

  const updateProcurementTypeOverrideNotify = (typeName, kind, uids) => {
    setTpl((prev) => {
      const byType = { ...(prev.procurement_by_jewellery_type || {}) };
      const cur = [...(byType[typeName] || [])];
      const idx = cur.findIndex((s) => s.kind === kind);
      const baseRow = (prev.procurement_defaults || []).find((s) => s.kind === kind);
      const baseDays = Number(baseRow?.sla_days || 0);
      const baseNotify = baseRow?.notify_user_ids || [];
      const label = baseRow?.label || kind;
      const cleaned = [...(uids || [])];
      if (idx >= 0) {
        cur[idx] = { ...cur[idx], notify_user_ids: cleaned };
        const stillOverridesDays = Number(cur[idx].sla_days || 0) !== baseDays;
        const stillOverridesNotify = !arraysEqual(cleaned, baseNotify);
        if (!stillOverridesDays && !stillOverridesNotify) {
          cur.splice(idx, 1);
        }
      } else if (!arraysEqual(cleaned, baseNotify)) {
        cur.push({ kind, label, sla_days: baseDays, notify_user_ids: cleaned });
      }
      if (cur.length === 0) delete byType[typeName];
      else byType[typeName] = cur;
      return { ...prev, procurement_by_jewellery_type: byType };
    });
  };

  // Resolve the current (possibly-overridden) notify list for a stage
  // in the currently-active type tab. Used by the NotifyPicker trigger
  // so it always shows the *effective* recipients for this tab.
  const isBaseTab = activeType === '__base__';
  const resolveNotifyForStage = (kind) => {
    if (isBaseTab) {
      return (tpl.stages || []).find((s) => s.kind === kind)?.notify_user_ids || [];
    }
    const ov = ((tpl.by_jewellery_type || {})[activeType] || []).find((s) => s.kind === kind);
    if (ov && ov.notify_user_ids !== undefined) return ov.notify_user_ids || [];
    return (tpl.stages || []).find((s) => s.kind === kind)?.notify_user_ids || [];
  };
  const resolveProcurementNotifyForStage = (kind) => {
    if (isBaseTab) {
      return (tpl.procurement_defaults || []).find((s) => s.kind === kind)?.notify_user_ids || [];
    }
    const ov = ((tpl.procurement_by_jewellery_type || {})[activeType] || []).find((s) => s.kind === kind);
    if (ov && ov.notify_user_ids !== undefined) return ov.notify_user_ids || [];
    return (tpl.procurement_defaults || []).find((s) => s.kind === kind)?.notify_user_ids || [];
  };
  const stageNotifyHasOverride = (kind) => {
    if (isBaseTab) return false;
    const ov = ((tpl.by_jewellery_type || {})[activeType] || []).find((s) => s.kind === kind);
    if (!ov || ov.notify_user_ids === undefined) return false;
    const base = (tpl.stages || []).find((s) => s.kind === kind)?.notify_user_ids || [];
    return !arraysEqual(ov.notify_user_ids || [], base);
  };
  const procurementNotifyHasOverride = (kind) => {
    if (isBaseTab) return false;
    const ov = ((tpl.procurement_by_jewellery_type || {})[activeType] || []).find((s) => s.kind === kind);
    if (!ov || ov.notify_user_ids === undefined) return false;
    const base = (tpl.procurement_defaults || []).find((s) => s.kind === kind)?.notify_user_ids || [];
    return !arraysEqual(ov.notify_user_ids || [], base);
  };

  const typeOverrideMap = (activeType && activeType !== '__base__')
    ? Object.fromEntries(((tpl.by_jewellery_type || {})[activeType] || []).map((s) => [s.kind, s.sla_days]))
    : {};

  const procurementTypeOverrideMap = (activeType && activeType !== '__base__')
    ? Object.fromEntries(((tpl.procurement_by_jewellery_type || {})[activeType] || []).map((s) => [s.kind, s.sla_days]))
    : {};

  const buildPayload = () => ({
    company_id: companyId,
    stages: (tpl.stages || []).map((s) => ({
      kind: s.kind, label: s.label, sla_days: Number(s.sla_days) || 0,
      notify_user_ids: [...(s.notify_user_ids || [])],
    })),
    by_jewellery_type: Object.fromEntries(
      Object.entries(tpl.by_jewellery_type || {}).map(([jt, rows]) => [
        jt, (rows || []).map((s) => ({
          kind: s.kind, label: s.label, sla_days: Number(s.sla_days) || 0,
          notify_user_ids: [...(s.notify_user_ids || [])],
        })),
      ]),
    ),
    procurement_defaults: (tpl.procurement_defaults || []).map((s) => ({
      kind: s.kind, label: s.label, sla_days: Number(s.sla_days) || 0,
      notify_user_ids: [...(s.notify_user_ids || [])],
    })),
    procurement_by_jewellery_type: Object.fromEntries(
      Object.entries(tpl.procurement_by_jewellery_type || {}).map(([jt, rows]) => [
        jt, (rows || []).map((s) => ({
          kind: s.kind, label: s.label, sla_days: Number(s.sla_days) || 0,
          notify_user_ids: [...(s.notify_user_ids || [])],
        })),
      ]),
    ),
    // Wave 3.99ah — Global Order Raised notify list (type-agnostic).
    order_raised_notify_user_ids: [...(tpl.order_raised_notify_user_ids || [])],
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/production-timeline/sla-template', {
        ...buildPayload(),
        // Wave 4.x — Cascade the notify changes to all active orders.
        backfill_active_orders: backfill,
      });
      const n = res?.data?.backfilled_active_orders;
      if (backfill && typeof n === 'number') {
        toast.success(n > 0
          ? `SLA template saved · notify recipients applied to ${n} active order${n === 1 ? '' : 's'}`
          : 'SLA template saved · no active orders needed updating');
      } else {
        toast.success('SLA template saved');
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Wave 4.x — Blast-radius preview. While "apply to all active orders" is
  // ticked, ask the backend how many active orders would actually change if
  // saved now. Debounced so live edits don't hammer the endpoint.
  useEffect(() => {
    if (!open || !backfill || !companyId) { setPreview(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post('/production-timeline/sla-template/backfill-preview', {
          ...buildPayload(),
          backfill_active_orders: true,
        });
        if (!cancelled) setPreview(data);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, backfill, companyId, tpl]);

  const activeOverrideCount = (
    Object.keys(tpl.by_jewellery_type || {}).length
    + Object.keys(tpl.procurement_by_jewellery_type || {}).length
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage SLA Template</DialogTitle>
          <DialogDescription>
            The default template pre-fills every new order — SLA days AND per-stage notify recipients. Per-jewellery-type overrides let you tune both per product (e.g. Necklace vs Ring).
          </DialogDescription>
        </DialogHeader>

        {/* Wave 3.99ah — Global "Order Raised" notify list. Applies to ALL
            jewellery types (not affected by the type tabs) and is notify-only
            (no SLA days, no delay tracking) — pinged the instant a new order
            is created. */}
        <div
          className="rounded-md border border-[var(--gold-accent)]/40 bg-[var(--paper-warm)] px-3 py-2.5 flex items-center justify-between gap-3"
          data-testid="sla-template-order-raised-row"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium text-[var(--ink-navy)] flex items-center gap-1.5">
              <Bell size={13} className="text-[var(--gold-accent)]" /> Order Raised
            </div>
            <div className="text-[11px] text-slate-500">
              Notified the moment a new order form is created — any jewellery type. Instant alert only (no SLA).
            </div>
          </div>
          <NotifyPicker
            users={users}
            value={tpl.order_raised_notify_user_ids || []}
            onChange={(uids) => updateOrderRaisedNotify(uids)}
            testId="sla-template-notify-order-raised"
          />
        </div>

        {/* Type selector row */}
        <div className="flex items-center flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setActiveType('__base__')}
            className={`text-[11px] px-2.5 py-1 rounded-md border ${activeType === '__base__' ? 'bg-[var(--ink-navy)] text-white border-[var(--ink-navy)]' : 'bg-white text-slate-700 border-[var(--divider)]'}`}
            data-testid="sla-template-tab-base"
          >
            Default
          </button>
          {jewelleryTypes.map((t) => {
            const hasOverride = Object.prototype.hasOwnProperty.call(tpl.by_jewellery_type || {}, t.name);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveType(t.name)}
                className={`text-[11px] px-2.5 py-1 rounded-md border ${activeType === t.name ? 'bg-[var(--ink-navy)] text-white border-[var(--ink-navy)]' : hasOverride ? 'bg-[var(--paper-warm)] text-[var(--ink-navy)] border-[var(--gold-accent)]' : 'bg-white text-slate-700 border-[var(--divider)]'}`}
                title={hasOverride ? 'Has override' : 'Inherits default'}
                data-testid={`sla-template-tab-${t.name}`}
              >
                {t.name}{hasOverride ? ' •' : ''}
              </button>
            );
          })}
        </div>

        <div className="space-y-1 max-h-[45vh] overflow-y-auto border-t border-[var(--divider)] pt-2">
          {(tpl.stages || []).flatMap((s) => {
            const isBase = activeType === '__base__';
            // Wave 3.90 — Replace the single "Fulfilling Requirements" row
            // with FOUR per-material rows (Gold / Diamond / Colour Stone /
            // Pearl). Each row is backed by ``procurement_defaults`` (base)
            // or ``procurement_by_jewellery_type[type]`` (per-type override).
            if (s.kind === 'fulfilling_requirements') {
              return (tpl.procurement_defaults || []).map((p) => {
                const baseDays = Number(p.sla_days || 0);
                const overrideDays = procurementTypeOverrideMap[p.kind];
                const hasOverride = overrideDays !== undefined;
                const effective = isBase ? baseDays : (hasOverride ? overrideDays : baseDays);
                const label = /requirement/i.test(p.label || '') ? p.label : `${p.label} Requirement`;
                const notify = resolveProcurementNotifyForStage(p.kind);
                const notifyOv = procurementNotifyHasOverride(p.kind);
                return (
                  <div key={`fulfilling-${p.kind}`} className="flex items-center gap-2 py-1.5 pl-4 border-l-2 border-[var(--divider)]" data-testid={`sla-template-procurement-row-${p.kind}`}>
                    <div className="flex-1 text-sm text-[var(--ink-navy)] min-w-0">
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Fulfilling ·</span>
                      {label}
                    </div>
                    <NotifyPicker
                      users={users}
                      value={notify}
                      hasOverride={notifyOv}
                      onChange={(uids) => {
                        if (isBase) updateProcurementBaseNotify(p.kind, uids);
                        else updateProcurementTypeOverrideNotify(activeType, p.kind, uids);
                      }}
                      testId={`sla-template-notify-procurement-${isBase ? 'base' : activeType}-${p.kind}`}
                    />
                    {!isBase && (
                      <div className="text-[10px] text-slate-500 w-16 text-right">
                        Def: <span className="font-mono tabular-nums">{baseDays}d</span>
                      </div>
                    )}
                    <div className="w-28 flex items-center gap-1.5">
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={effective}
                        placeholder={isBase ? undefined : 'inherit'}
                        onChange={(e) => {
                          if (isBase) updateProcurementBase(p.kind, e.target.value);
                          else updateProcurementTypeOverride(activeType, p.kind, e.target.value);
                        }}
                        className={`text-right font-mono tabular-nums ${!isBase && hasOverride ? 'border-[var(--gold-accent)]' : ''}`}
                        data-testid={`sla-template-input-procurement-${activeType === '__base__' ? 'base' : activeType}-${p.kind}`}
                      />
                      <span className="text-xs text-slate-500">d</span>
                    </div>
                    {!isBase && hasOverride && (
                      <button
                        type="button"
                        onClick={() => updateProcurementTypeOverride(activeType, p.kind, '')}
                        className="text-[10px] text-slate-400 hover:text-[var(--ink-navy)]"
                        title="Reset to default"
                        data-testid={`sla-template-reset-procurement-${activeType}-${p.kind}`}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              });
            }
            const baseDays = Number(s.sla_days || 0);
            const overrideDays = typeOverrideMap[s.kind];
            const hasOverride = overrideDays !== undefined;
            const effective = isBase ? baseDays : (hasOverride ? overrideDays : baseDays);
            const notify = resolveNotifyForStage(s.kind);
            const notifyOv = stageNotifyHasOverride(s.kind);
            return [(
              <div key={s.kind} className="flex items-center gap-2 py-1.5">
                <div className="flex-1 text-sm text-[var(--ink-navy)] min-w-0">{slaConfigStageLabel(s.kind, s.label)}</div>
                <NotifyPicker
                  users={users}
                  value={notify}
                  hasOverride={notifyOv}
                  onChange={(uids) => {
                    if (isBase) updateBaseNotify(s.kind, uids);
                    else updateTypeOverrideNotify(activeType, s.kind, uids);
                  }}
                  testId={`sla-template-notify-${isBase ? 'base' : activeType}-${s.kind}`}
                />
                {!isBase && (
                  <div className="text-[10px] text-slate-500 w-16 text-right">
                    Def: <span className="font-mono tabular-nums">{baseDays}d</span>
                  </div>
                )}
                <div className="w-28 flex items-center gap-1.5">
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={effective}
                    placeholder={isBase ? undefined : 'inherit'}
                    onChange={(e) => {
                      if (isBase) updateBaseDays(s.kind, e.target.value);
                      else updateTypeOverride(activeType, s.kind, e.target.value);
                    }}
                    className={`text-right font-mono tabular-nums ${!isBase && hasOverride ? 'border-[var(--gold-accent)]' : ''}`}
                    data-testid={`sla-template-input-${activeType === '__base__' ? 'base' : activeType}-${s.kind}`}
                  />
                  <span className="text-xs text-slate-500">d</span>
                </div>
                {!isBase && hasOverride && (
                  <button
                    type="button"
                    onClick={() => updateTypeOverride(activeType, s.kind, '')}
                    className="text-[10px] text-slate-400 hover:text-[var(--ink-navy)]"
                    title="Reset to default"
                    data-testid={`sla-template-reset-${activeType}-${s.kind}`}
                  >
                    ✕
                  </button>
                )}
              </div>
            )];
          })}
        </div>

        {activeOverrideCount > 0 && (
          <div className="text-[11px] text-slate-500 pt-1">
            {activeOverrideCount} jewellery type{activeOverrideCount === 1 ? '' : 's'} with custom SLA.
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <label
            className="flex items-start gap-2 text-left cursor-pointer select-none sm:mr-auto"
            data-testid="sla-template-backfill-label"
          >
            <Checkbox
              checked={backfill}
              onCheckedChange={(v) => setBackfill(!!v)}
              className="mt-0.5"
              data-testid="sla-template-backfill-checkbox"
            />
            <span className="text-[12px] leading-snug text-[var(--ink-navy)]">
              Also apply to all active orders
              <span className="block text-[11px] text-slate-500">
                Replaces the notify recipients on every active order (excludes Completed / Cancelled) with this template.
              </span>
              {backfill && (
                <span className="mt-1 block" data-testid="sla-template-backfill-preview">
                  {previewLoading ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                      <Loader2 size={11} className="animate-spin" /> Checking impact…
                    </span>
                  ) : preview ? (
                    preview.will_change > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-[var(--gold-soft,#fdf6e3)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--ink-navy)] border border-[var(--gold-accent)]/40">
                        <AlertTriangle size={11} className="text-[var(--gold-accent)]" />
                        {preview.will_change} of {preview.total_active} active order{preview.total_active === 1 ? '' : 's'} will change
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
                        <Check size={11} /> No active orders need updating{preview.total_active ? ` (${preview.total_active} checked)` : ''}
                      </span>
                    )
                  ) : null}
                </span>
              )}
            </span>
          </label>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="sla-template-save-button">
              {saving ? 'Saving…' : 'Save Template'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
